import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Badge,
  Alert,
  Spinner,
  Modal,
  Tabs,
  Tab
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { getCourseCodes, generateCourseCodes, getCourses, updateCourseCodeExpiryDate, deleteCourseCode } from '../../services/courseService';
import { CourseCode, Course } from '../../types';
import CourseCodesList from '../../components/teacher/CourseCodesList';
import CourseCodesReports from '../../components/teacher/CourseCodesReports';
import { FaPlus, FaCopy, FaTrash, FaCalendarAlt, FaClock, FaFileDownload, FaFilePdf, FaPrint } from 'react-icons/fa';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

const CourseCodes: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedCourseDetails, setSelectedCourseDetails] = useState<Course | null>(null);
  const [codes, setCodes] = useState<CourseCode[]>([]);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [codeCount, setCodeCount] = useState<number>(1);
  const [expiryDays, setExpiryDays] = useState<number>(30);
  const [activeTab, setActiveTab] = useState<string>('codes');
  const [generating, setGenerating] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [codesPerPage, setCodesPerPage] = useState<number>(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // حفظ إعدادات المستخدم في التخزين المحلي
  useEffect(() => {
    // استرجاع إعدادات المستخدم من التخزين المحلي عند تحميل الصفحة
    const savedCodesPerPage = localStorage.getItem('codesPerPage');
    if (savedCodesPerPage) {
      setCodesPerPage(parseInt(savedCodesPerPage));
    }
  }, []);

  // حفظ إعدادات عدد الأكواد في الصفحة الواحدة
  useEffect(() => {
    localStorage.setItem('codesPerPage', codesPerPage.toString());
  }, [codesPerPage]);

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      loadCodes();
      // الحصول على تفاصيل الكورس المحدد
      const course = courses.find(c => c.id === selectedCourse);
      if (course) {
        setSelectedCourseDetails(course);
      }
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (codes.length > 0) {
      console.log('جميع الأكواد:', codes);
      console.log('الأكواد المستخدمة:', codes.filter(code => code.isUsed === true && code.usedBy));
      console.log('الأكواد غير المستخدمة:', codes.filter(code => code.isUsed === false && !code.isExpired));
      console.log('الأكواد منتهية الصلاحية:', codes.filter(code => code.isExpired === true));
    }
  }, [codes]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const coursesData = await getCourses();
      setCourses(coursesData);
      if (coursesData.length > 0) {
        setSelectedCourse(coursesData[0].id);
      }
      setLoading(false);
    } catch (error) {
      setError('حدث خطأ أثناء تحميل الكورسات');
      setLoading(false);
    }
  };

  const loadCodes = async () => {
    if (!selectedCourse) return;
    
    try {
      setLoading(true);
      if (!currentUser?.uid) {
        throw new Error('يجب تسجيل الدخول أولاً');
      }
      const codesData = await getCourseCodes(currentUser.uid, true);
      // تصفية الأكواد حسب الكورس المحدد
      const filteredCodes = codesData.filter(code => code.courseId === selectedCourse);
      setCodes(filteredCodes);
      setLoading(false);
    } catch (error) {
      setError('حدث خطأ أثناء تحميل أكواد التفعيل');
      setLoading(false);
    }
  };

  const handleGenerateCodes = async () => {
    try {
      setGenerating(true);
      setError('');
      
      if (!currentUser?.uid) {
        throw new Error('يجب تسجيل الدخول أولاً');
      }
      
      await generateCourseCodes(selectedCourse, codeCount, expiryDays, currentUser.uid);
      await loadCodes();
      setShowModal(false);
      setGenerating(false);
    } catch (error) {
      setError('حدث خطأ أثناء إنشاء أكواد التفعيل');
      setGenerating(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const handleDeleteCode = async (codeId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الكود؟')) {
      try {
        await deleteCourseCode(codeId);
        await loadCodes();
      } catch (error) {
        setError('حدث خطأ أثناء حذف الكود');
      }
    }
  };

  const handleUpdateExpiry = async (codeId: string, days: number) => {
    try {
      await updateCourseCodeExpiryDate(codeId, days);
      await loadCodes();
    } catch (error) {
      setError('حدث خطأ أثناء تحديث تاريخ انتهاء الصلاحية');
    }
  };

  // تطبيق الفلاتر على الأكواد
  const filteredCodes = codes.filter(code => {
    // فلتر البحث
    const matchesSearch = code.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    // فلتر الحالة
    let matchesStatus = true;
    if (statusFilter === 'used') {
      // تحسين التحقق من الأكواد المستخدمة
      const isUsed = code.isUsed === true;
      const hasUser = typeof code.usedBy === 'string' && code.usedBy.trim() !== '';
      matchesStatus = isUsed && hasUser;
    } else if (statusFilter === 'unused') {
      matchesStatus = code.isUsed === false && !code.isExpired;
    } else if (statusFilter === 'expired') {
      matchesStatus = code.isExpired === true;
    }
    
    return matchesSearch && matchesStatus;
  });

  // ترتيب الأكواد: الغير مستخدمة ثم المنتهية ثم المستخدمة
  const sortedCodes = [...filteredCodes].sort((a, b) => {
    // حساب الأيام المتبقية للانتهاء
    const now = Date.now();
    const daysLeftA = a.expiryDate ? Math.ceil((a.expiryDate - now) / (1000 * 60 * 60 * 24)) : 0;
    const daysLeftB = b.expiryDate ? Math.ceil((b.expiryDate - now) / (1000 * 60 * 60 * 24)) : 0;
    
    // الأكواد الغير مستخدمة في الأول
    if (!a.isUsed && a.isExpired !== true && b.isUsed) return -1;
    if (a.isUsed && !b.isUsed && b.isExpired !== true) return 1;
    
    // ترتيب الأكواد الغير مستخدمة حسب تاريخ الانتهاء (الأقرب للانتهاء في الأول)
    if (!a.isUsed && !b.isUsed && a.isExpired !== true && b.isExpired !== true) {
      return daysLeftA - daysLeftB;
    }
    
    // الأكواد المنتهية في المنتصف
    if (a.isExpired && !b.isExpired && b.isUsed) return -1;
    if (!a.isExpired && a.isUsed && b.isExpired) return 1;
    
    // ترتيب الأكواد المنتهية حسب تاريخ الانتهاء (الأحدث انتهاءً في الأول)
    if (a.isExpired && b.isExpired) {
      return (b.expiryDate || 0) - (a.expiryDate || 0);
    }
    
    // الأكواد المستخدمة في النهاية
    if (a.isUsed && b.isUsed) {
      return (b.usedAt || 0) - (a.usedAt || 0); // الأحدث استخدامًا في الأول
    }
    
    return 0;
  });

  // حساب عدد الصفحات
  const totalPages = Math.ceil(sortedCodes.length / codesPerPage);
  
  // الحصول على الأكواد للصفحة الحالية
  const indexOfLastCode = currentPage * codesPerPage;
  const indexOfFirstCode = indexOfLastCode - codesPerPage;
  const currentCodes = sortedCodes.slice(indexOfFirstCode, indexOfLastCode);

  // تغيير الصفحة
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getCodeStatus = (code: CourseCode): string => {
    if (code.isExpired) return 'منتهي الصلاحية';
    // تحسين التحقق من الأكواد المستخدمة
    const isUsed = code.isUsed === true;
    const hasUser = typeof code.usedBy === 'string' && code.usedBy.trim() !== '';
    if (isUsed && hasUser) return 'مستخدم';
    return 'متاح';
  };

  const getStatusBadgeVariant = (status: string): string => {
    switch (status) {
      case 'متاح': return 'success';
      case 'مستخدم': return 'primary';
      case 'منتهي الصلاحية': return 'danger';
      default: return 'secondary';
    }
  };

  // دالة لتصدير البيانات إلى ملف PDF
  const exportToPDF = () => {
    if (!selectedCourseDetails) return;
    
    // إنشاء مستند PDF بتوجيه أفقي
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true
    });
    
    // استخدام الخط الافتراضي
    doc.setFont('helvetica');
    
    // إضافة عنوان للتقرير
    doc.setFontSize(16);
    doc.text('Course Codes Report', 150, 10, { align: 'center' });
    doc.text(selectedCourseDetails.title, 150, 20, { align: 'center' });
    
    // إضافة تاريخ التقرير
    doc.setFontSize(10);
    doc.text(`Report Date: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, 150, 30, { align: 'center' });
    
    // إضافة إحصائيات
    doc.setFontSize(12);
    doc.text(`Total Codes: ${sortedCodes.length}`, 20, 40);
    doc.text(`Used Codes: ${sortedCodes.filter(code => code.isUsed).length}`, 20, 45);
    doc.text(`Available Codes: ${sortedCodes.filter(code => !code.isUsed && !code.isExpired).length}`, 20, 50);
    doc.text(`Expired Codes: ${sortedCodes.filter(code => code.isExpired).length}`, 20, 55);
    
    // إنشاء جدول البيانات باستخدام autoTable
    import('jspdf-autotable').then(x => {
      const { default: autoTable } = x;
      
      // تحويل البيانات إلى مصفوفة
      const tableColumn = ["#", "Code", "Status", "Creation Date", "Expiry Date"];
      const tableRows = sortedCodes.map((code, index) => {
        let status = "Available";
        if (code.isExpired) status = "Expired";
        if (code.isUsed) status = "Used";
        
        return [
          index + 1,
          code.code,
          status,
          format(code.createdAt, 'yyyy-MM-dd'),
          code.expiryDate ? format(code.expiryDate, 'yyyy-MM-dd') : '-'
        ];
      });
      
      // إضافة الجدول إلى المستند
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 65,
        styles: { 
          halign: 'center',
          fontSize: 10
        },
        headStyles: { 
          fillColor: [41, 128, 185], 
          textColor: 255,
          fontSize: 12,
          halign: 'center'
        },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        theme: 'grid',
        tableWidth: 'auto'
      });
      
      // التحقق إذا كان المستخدم يستخدم هاتف محمول
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // حفظ المستند
      if (isMobile) {
        // على الأجهزة المحمولة، قم بتحميل الملف مباشرة
        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        
        // إنشاء رابط وهمي للتحميل
        const link = document.createElement('a');
        link.href = url;
        link.download = `Course-Codes-${selectedCourseDetails.title}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // على أجهزة الكمبيوتر، فتح الملف في نافذة جديدة
        window.open(URL.createObjectURL(doc.output('blob')));
      }
    });
  };

  // دالة للطباعة
  const printCodes = () => {
    if (!selectedCourseDetails) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const html = `
      <html dir="rtl">
        <head>
          <title>أكواد الدورة: ${selectedCourseDetails.title}</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
            th { background-color: #f2f2f2; }
            .header { text-align: center; margin-bottom: 20px; }
            .stats { display: flex; justify-content: space-around; margin: 20px 0; }
            .stat-box { border: 1px solid #ddd; padding: 10px; text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>أكواد الدورة: ${selectedCourseDetails.title}</h2>
            <p>تاريخ التقرير: ${format(new Date(), 'yyyy-MM-dd HH:mm', { locale: ar })}</p>
          </div>
          
          <div class="stats">
            <div class="stat-box">
              <h4>إجمالي الأكواد</h4>
              <p>${sortedCodes.length}</p>
            </div>
            <div class="stat-box">
              <h4>الأكواد المستخدمة</h4>
              <p>${sortedCodes.filter(code => code.isUsed).length}</p>
            </div>
            <div class="stat-box">
              <h4>الأكواد غير المستخدمة</h4>
              <p>${sortedCodes.filter(code => !code.isUsed && !code.isExpired).length}</p>
            </div>
            <div class="stat-box">
              <h4>الأكواد منتهية الصلاحية</h4>
              <p>${sortedCodes.filter(code => code.isExpired).length}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>الكود</th>
                <th>الحالة</th>
                <th>تاريخ الإنشاء</th>
                <th>تاريخ الانتهاء</th>
              </tr>
            </thead>
            <tbody>
              ${sortedCodes.map((code, index) => {
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${code.code}</td>
                    <td>${getCodeStatus(code)}</td>
                    <td>${format(code.createdAt, 'yyyy-MM-dd HH:mm', { locale: ar })}</td>
                    <td>${code.expiryDate ? format(code.expiryDate, 'yyyy-MM-dd HH:mm', { locale: ar }) : 'غير محدد'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading && !codes.length) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" />
          <p>جاري التحميل...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4 text-center">إدارة أكواد التفعيل</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>اختر الكورس</Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedCourse} 
                  onChange={(e) => setSelectedCourse(e.target.value)}
                >
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end justify-content-end">
              <Button 
                variant="primary" 
                onClick={() => setShowModal(true)}
                className="mb-2"
              >
                <FaPlus className="me-1" /> إنشاء أكواد جديدة
              </Button>
            </Col>
          </Row>
          
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'codes')}
            className="mb-3"
          >
            <Tab eventKey="codes" title="قائمة الأكواد">
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>بحث:</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="ابحث عن كود..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>فلتر الحالة:</Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1); // إعادة تعيين الصفحة الحالية عند تغيير الفلتر
                      }}
                    >
                      <option value="all">جميع الأكواد</option>
                      <option value="used">الأكواد المستخدمة</option>
                      <option value="unused">الأكواد المتاحة</option>
                      <option value="expired">الأكواد منتهية الصلاحية</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>عدد الأكواد في الصفحة:</Form.Label>
                    <Form.Select
                      value={codesPerPage}
                      onChange={(e) => {
                        setCodesPerPage(parseInt(e.target.value));
                        setCurrentPage(1); // إعادة تعيين الصفحة الحالية عند تغيير عدد الأكواد
                      }}
                    >
                      <option value="10">10 أكواد</option>
                      <option value="50">50 كود</option>
                      <option value="100">100 كود</option>
                      <option value="150">150 كود</option>
                      <option value="200">200 كود</option>
                      <option value="300">300 كود</option>
                      <option value="400">400 كود</option>
                      <option value="500">500 كود</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end justify-content-end">
                  {sortedCodes.length > 0 && (
                    <Badge bg="info" className="p-2 mb-2">
                      إجمالي الأكواد: {sortedCodes.length}
                    </Badge>
                  )}
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="mx-1"
                    onClick={exportToPDF}
                  >
                    <FaFilePdf className="me-1" /> تصدير إلى PDF
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={printCodes}
                  >
                    <FaPrint className="me-1" /> طباعة
                  </Button>
                </Col>
              </Row>
              
              {sortedCodes.length === 0 ? (
                <Alert variant="info">لا توجد أكواد لهذا الكورس. قم بإنشاء أكواد جديدة.</Alert>
              ) : (
                <>
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>الكود</th>
                        <th>الحالة</th>
                        <th>تاريخ الإنشاء</th>
                        <th>تاريخ الانتهاء</th>
                        <th>إجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCodes.map((code, index) => {
                        const status = getCodeStatus(code);
                        return (
                          <tr key={code.id}>
                            <td>{indexOfFirstCode + index + 1}</td>
                            <td>
                              <code>{code.code}</code>
                            </td>
                            <td>
                              <Badge bg={getStatusBadgeVariant(status)}>{status}</Badge>
                            </td>
                            <td>{new Date(code.createdAt).toLocaleDateString('ar-EG')}</td>
                            <td>
                              {code.expiryDate ? (
                                new Date(code.expiryDate).toLocaleDateString('ar-EG')
                              ) : (
                                'غير محدد'
                              )}
                            </td>
                            <td>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-1"
                                onClick={() => handleCopyCode(code.code)}
                                title="نسخ الكود"
                              >
                                <FaCopy />
                              </Button>
                              {!code.isUsed && (
                                <>
                                  <Button 
                                    variant="outline-warning" 
                                    size="sm" 
                                    className="me-1"
                                    onClick={() => handleUpdateExpiry(code.id, 30)}
                                    title="تمديد الصلاحية 30 يوم"
                                  >
                                    <FaCalendarAlt />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDeleteCode(code.id)}
                                    title="حذف الكود"
                                  >
                                    <FaTrash />
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            السابق
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li 
                            key={i + 1} 
                            className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                          >
                            <button 
                              className="page-link" 
                              onClick={() => paginate(i + 1)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            التالي
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </Tab>
            <Tab eventKey="reports" title="تقارير الاستخدام">
              {selectedCourseDetails && (
                <CourseCodesReports course={selectedCourseDetails} />
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
      
      {/* Modal for generating new codes */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>إنشاء أكواد تفعيل جديدة</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>عدد الأكواد</Form.Label>
              <Form.Control 
                type="number" 
                min="1" 
                max="100"
                value={codeCount} 
                onChange={(e) => setCodeCount(parseInt(e.target.value) || 1)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>مدة الصلاحية (بالأيام)</Form.Label>
              <Form.Control 
                type="number" 
                min="1" 
                max="365"
                value={expiryDays} 
                onChange={(e) => setExpiryDays(parseInt(e.target.value) || 30)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            إلغاء
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGenerateCodes}
            disabled={generating}
          >
            {generating ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                جاري الإنشاء...
              </>
            ) : (
              'إنشاء الأكواد'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CourseCodes;
